import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from './base.service';
import { City as CityModel } from '../interfaces/city.interface';
import { PackageModel, VehicleModel } from '../interfaces/vehicle-model.interface';
import { ClientDetails } from '../../models/client-details.model';

export interface SearchRequest {
  clientId: number;
  searchFor: 'PACKAGE' | 'VEHICLE' | 'PACKAGEDETAILS';
  vehicleTypeId: number;
  packageId: number;
  fromDate: string;
  toDate: string;
  cityId?: number;
  packageTypeId?: number;
  pickupTime?: string;
}

export interface ReportingTimeValidation {
  reportingTime: string;
  tripFromDate: string;
}

const SEARCH_FORM_STORAGE_KEY = 'mfmb2c_tripSearchFormData';
const SELECTED_PACKAGE_STORAGE_KEY = 'mfmb2c_selectedPackage';

@Injectable({
  providedIn: 'root'
})
export class TripSearchService {
  constructor(
    private baseService: BaseService,
    private http: HttpClient
  ) {
    this.hydrateFromSessionStorage();
  }

  searchFormData = signal<any>(null);
  /** null = no search loaded yet (avoids treating initial empty array as a completed search). */
  searchVehicleData = signal<any[] | null>(null);
  selectedPackage = signal<any>(null);

  private hydrateFromSessionStorage(): void {
    try {
      const rawForm = sessionStorage.getItem(SEARCH_FORM_STORAGE_KEY);
      if (rawForm) {
        this.searchFormData.set(JSON.parse(rawForm));
      }
      const rawPackage = sessionStorage.getItem(SELECTED_PACKAGE_STORAGE_KEY);
      if (rawPackage) {
        this.selectedPackage.set(JSON.parse(rawPackage));
      }
    } catch {
      sessionStorage.removeItem(SEARCH_FORM_STORAGE_KEY);
      sessionStorage.removeItem(SELECTED_PACKAGE_STORAGE_KEY);
    }
  }

  private persistSearchFormData(data: any): void {
    try {
      if (data == null) {
        sessionStorage.removeItem(SEARCH_FORM_STORAGE_KEY);
      } else {
        sessionStorage.setItem(SEARCH_FORM_STORAGE_KEY, JSON.stringify(data));
      }
    } catch {
      /* ignore quota / private mode */
    }
  }

  private persistSelectedPackage(data: any): void {
    try {
      if (data == null) {
        sessionStorage.removeItem(SELECTED_PACKAGE_STORAGE_KEY);
      } else {
        sessionStorage.setItem(SELECTED_PACKAGE_STORAGE_KEY, JSON.stringify(data));
      }
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * Clear the search form data
   */
  clearSearchFormData(): void {
    this.searchFormData.set(null);
    this.persistSearchFormData(null);
  }

  /**
   * Get the current search form data
   */
  getSearchFormData(): any {
    return this.searchFormData();
  }

  getSearchVehicleData(): any {
    return this.searchVehicleData();
  }

  /**
   * Set the search form data
   */
  setSearchFormData(data: any): void {
    this.searchFormData.set(data);
    this.persistSearchFormData(data);
  }

  setSearchVehicleData(data: any): void {
    this.searchVehicleData.set(data == null ? null : data);
  }

  /**
   * Same endpoint as trip-search "Explore Cabs" — searchFor PACKAGEDETAILS returns the booking-search card list.
   */
  fetchPackageDetailsList(searchParams: SearchRequest): Observable<any[]> {
    const apiUrl = '/api/B2COnlineBooking/SearchVehicleOrPackageNew';
    return this.baseService.postData(apiUrl, searchParams).pipe(
      map((response: any) => {
        if (response.responseCode === 'OK') {
          return (response.data ?? []) as any[];
        }
        throw new Error(response.responseMessage || 'Failed to load search results');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Set the selected package for booking
   */
  setSelectedPackage(data: any): void {
    this.selectedPackage.set(data);
    this.persistSelectedPackage(data);
  }

  /**
   * Get the selected package
   */
  getSelectedPackage(): any {
    return this.selectedPackage();
  }

  /**
   * Clear the selected package
   */
  clearSelectedPackage(): void {
    this.selectedPackage.set(null);
    this.persistSelectedPackage(null);
  }

  /**
   * Load client details
   */
  loadClientDetails(): Observable<ClientDetails> {
    const apiUrl = '/api/B2COnlineBooking/B2CclientDetails';
    
    return this.baseService.getData(apiUrl).pipe(
      map((response: any) => {
        if (response.responseCode === 'OK') {
          return response.data as ClientDetails;
        } else {
          throw new Error(response.responseMessage || 'Failed to load client details');
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Load cities for a specific client
   */
loadCities(clientId: number): Observable<CityModel[]> {
    if (!clientId) {
      return throwError(() => new Error('Client ID is required'));
    }

    const apiUrl = `/api/B2COnlineBooking/${clientId}/GetServingCity`;
    
    return this.baseService.getData(apiUrl).pipe(
      map((response: any) => {
        if (response.responseCode === 'OK') {
          return response.data as CityModel[];
        } else {
          throw new Error(response.responseMessage || 'Failed to load cities');
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Load packages based on search criteria
   */
  loadPackages(searchParams: SearchRequest): Observable<PackageModel[]> {
    const apiUrl = '/api/B2COnlineBooking/SearchVehicleOrPackageNew';
    debugger;
    return this.baseService.postData(apiUrl, searchParams).pipe(
      map((response: any) => {
        if (response.responseCode === 'OK') {
          return response.data as PackageModel[];
        } else {
          throw new Error(response.responseMessage || 'Failed to load packages');
        }
      }),
      catchError((error) => {
        return this.handleError(error);
      })
    );
  }

  /**
   * Load vehicles based on search criteria
   */
  loadVehicles(searchParams: SearchRequest): Observable<VehicleModel[]> {
    const apiUrl = '/api/B2COnlineBooking/SearchVehicleOrPackageNew';
    
    return this.baseService.postData(apiUrl, searchParams).pipe(
      map((response: any) => {
        if (response.responseCode === 'OK') {
          return response.data as VehicleModel[];
        } else {
          throw new Error(response.responseMessage || 'Failed to load vehicles');
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Load vehicle types based on search criteria
   */
  loadVehicleTypes(searchParams: SearchRequest): Observable<VehicleModel[]> {
    const apiUrl = '/api/B2COnlineBooking/SearchVehicleOrPackageNew';
    
    return this.baseService.postData(apiUrl, searchParams).pipe(
      map((response: any) => {
        if (response.responseCode === 'OK') {
          // Handle different possible data structures
          let vehicleData = response.data;
          
          // Check if data is nested in a different structure
          if (response.data && typeof response.data === 'object') {
            if (response.data.vehicleTypes) {
              vehicleData = response.data.vehicleTypes;
            } else if (response.data.vehicles) {
              vehicleData = response.data.vehicles;
            } else if (response.data.data) {
              vehicleData = response.data.data;
            }
          }
          
          return vehicleData as VehicleModel[];
        } else {
          throw new Error(response.responseMessage || 'Failed to load vehicle types');
        }
      }),
      catchError((error) => {
        return this.handleError(error);
      })
    );
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Observable<never> {
    return throwError(() => error);
  }


  validateReportingTime(params: ReportingTimeValidation): Observable<boolean> {
    debugger;
    return this.baseService.postData('/api/B2COnlineBooking/ValidateReportingTime', params).pipe(
      map((response: any) => {
       if(response.responseCode === 'OK'){
        return true;
       }
       return false;
      }),
      catchError((error) => {
        return of(false);
        //return throwError(() => error);
      })

    );
  }

  validateReportingTimeN(params: ReportingTimeValidation): Observable<string> {
    return this.baseService.postData('/api/B2COnlineBooking/ValidateReportingTime', params).pipe(
        map((response: any) => {
          debugger;
          if (response?.responseCode === 'OK') {
             return response?.responseMessage;
          }
          return response?.responseMessage;
        }),
        catchError((error) => {
          debugger;
          if (error.status === 400) {
            return of(error.error.responseMessage);
          }
          return of(error.error.responseMessage);
        })
      );
  }
  



} 